import { InfoCircleOutlined, UploadOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Select,
  Switch,
  Tag,
  Tooltip,
  Upload,
} from 'antd';
import { FormInstance } from 'antd/es/form/Form';
import Cookies from 'js-cookie';
import { debounce, find, isEmpty, sumBy } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAsync } from 'react-use';
import styled from 'styled-components';

import { REGIONS } from '../../constants/REGIONS';
import { fetchAPI, getFullAPIURLWithEndpoint } from '../../utils/api';
import { getOrderDetail } from '../Order/OrderListing';
import OrderProductList from './OrderProductList';
import OrderSyncSMEPODocuments from './OrderSyncSMEPODocuments';
import { ColorMap, OrderPaymentStatus } from './utils';

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

const formItemLayoutWithOutLabel = {
  wrapperCol: { span: 16, offset: 8 },
};

const validateMessages = {
  required: 'This field is required!',
  types: {
    email: 'Not a validate email!',
    number: 'Not a validate number!',
  },
  number: {
    range: 'Must be between ${min} and ${max}', // eslint-disable-line
  },
};
export const normFile = e => {
  if (Array.isArray(e)) {
    return e;
  }
  return e && e.fileList;
};

type EntityOption = {
  id: number;
  name: string;
  region?: string;
  payment_due_in_days?: number;
  billing_address_1?: string;
  billing_addresses?: Array<{ address_name?: string }>;
  general_config?: { invoice_item_dp?: number } | null;
};

type NewBuyerPayload = {
  name: string;
  email: string;
  phone_number?: string;
  shipping_addresses?: string[];
};

export default (props: {
  form: FormInstance<any>;
  users?: any;
  onOk: any;
  refreshDetailData?: any;
}) => {
  const { form, users } = props;
  const user = useSelector((state: AppState) => state.user);
  const id = form.getFieldValue('id');
  const CHANGE_REASON_OPTIONS = [
    { label: 'Price correction', value: 'Price correction' },
    { label: 'Quantity adjustment', value: 'Quantity adjustment' },
    {
      label: 'Product specification update',
      value: 'Product specification update',
    },
    { label: 'Supplier change', value: 'Supplier change' },
    { label: 'Customer request', value: 'Customer request' },
    { label: 'Other', value: '__other__' },
  ];
  const buyerId = form.getFieldValue('buyer_id') || '';
  const buyerName = form.getFieldValue('buyer_name') || '';
  const buyerEmail = form.getFieldValue('buyer_email') || '';
  const requesterId = form.getFieldValue('requester_id') || null;
  const requesterName = form.getFieldValue('requester_name') || '';
  const requesterEmail = form.getFieldValue('requester_email') || '';
  const requesterPhone = form.getFieldValue('requester_phone_number') || '';
  const hasAttachmentPO = form.getFieldValue('has_attachment_po') || '';
  const defaultFileList = form.getFieldValue('attachments') || [];
  // const isFinance =
  //   user.role === 'superadmin' ||
  //   user.team === 'FINANCE' ||
  //   user.team === 'FINANCE EXCLUSIVE';
  // const operationStatus = form.getFieldValue('operation_status');
  const [currency, setCurrency] = useState(
    form.getFieldValue('currency') || 'SGD'
  );
  const [region, setRegion] = useState(form.getFieldValue('region'));
  const [useRegionTax, setUseRegionTax] = useState(
    form.getFieldValue('is_region_tax')
  );
  const [subtotal, setSubtotal] = useState(0);
  const [total, setTotal] = useState(0);
  const [shippingOptions, setShippingOptions] = useState([]);
  const [buyers, setBuyers] = useState({
    list: buyerId
      ? [
          {
            id: buyerId,
            name: buyerName,
            email: buyerEmail,
            shipping_addresses: [],
            phone_number: null,
            entity: null,
          },
        ]
      : [],
    q: '',
    loading: false,
  });
  const [requesters, setRequesters] = useState({
    list: requesterId
      ? [
          {
            id: requesterId,
            name: requesterName,
            email: requesterEmail,
            shipping_addresses: [],
            phone_number: null,
            entity: null,
          },
        ]
      : [],
    q: '',
    loading: false,
  });
  const [manualRequester, setManualRequester] = useState({
    requester_id: requesterId,
    requester_email: requesterEmail,
    requester_name: requesterName,
    requester_phone_number: requesterPhone,
  });
  // the Recipient Select is uncontrolled otherwise (Form.Item has no name),
  // so entity changes would never blank the visible selection
  const [recipientValue, setRecipientValue] = useState<string | undefined>(
    String(buyerId) || undefined
  );
  const buyerTimer = useRef(null);
  const requesterTimer = useRef(null);
  const [entities, setEntities] = useState<{
    list: EntityOption[];
    loading: boolean;
  }>({
    list: [],
    loading: false,
  });
  const [selectedEntity, setSelectedEntity] = useState<EntityOption | null>(
    null
  );
  const [showEntityName, setShowEntityName] = useState(false);
  const [entityFilters, setEntityFilters] = useState({ page: 1, q: '' });
  const debounceEntityQ = debounce(
    val => setEntityFilters({ page: 1, q: val }),
    500
  );
  const entityTimer = useRef(null);

  const customProductCreateBtn: any = useRef();
  const standardProductCreateBtn: any = useRef();
  const jdProductCreateBtn: any = useRef();
  const amazonProductCreateBtn: any = useRef();
  const [isNcpr, setIsNcpr] = useState(false);
  const [isUseDefaultBillingAddress, setIsUseDefaultBillingAddress] = useState(
    true
  );
  const [shouldFilterByRegion, setShouldFilterByRegion] = useState(true);
  const [selectedBuyerAddresses, setSelectedBuyerAddresses] = useState([]);
  const [decimalPoint, setDecimalPoint] = useState(2);
  const [changeReasonModalOpen, setChangeReasonModalOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const changeReasonPromiseRef = useRef(null);
  const [createBuyerOpen, setCreateBuyerOpen] = useState(false);
  const [entityBuyerForm] = Form.useForm();

  const categories = useAsync<any>(async () => {
    const data = await fetchAPI('orders/items/category');

    return data.reduce((arr, cat) => {
      const obj = {
        value: cat.id,
        label: cat.name,
      };

      const childrens =
        cat.children?.map(catChild => ({
          value: catChild.id,
          label: `-- ${catChild.name}`,
        })) || [];

      return [...arr, obj, ...childrens];
    }, []);
  });

  // const treeDataForCategories = get(categories, 'value.items', []).map(
  //   item => ({
  //     title: item.name,
  //     value: item.name,
  //     children: item.children.map(child => ({
  //       title: child.name,
  //       value: child.name,
  //     })),
  //   })
  // );

  const getBuyers = useCallback(
    async (q, regionVal = null) => {
      setBuyers(b => ({ ...b, loading: true }));
      try {
        let url = `entity/buyer/list?page=1&per_page=30&q=${q || ''}`;

        if (shouldFilterByRegion && regionVal) {
          url += `&region=${regionVal}`;
        }

        const data = await fetchAPI(url);

        setBuyers(b => ({ ...b, loading: false, list: data.items }));
        return data.items;
      } catch (error) {
        message.error('Failed get buyers');
        setBuyers(b => ({ ...b, loading: false, list: [] }));
        return [];
      }
    },
    [shouldFilterByRegion]
  );

  const getRequesters = useCallback(
    async (q, regionVal = null) => {
      setRequesters(b => ({ ...b, loading: true }));
      try {
        let url = `entity/buyer/list?page=1&per_page=30&q=${q || ''}`;

        if (shouldFilterByRegion && regionVal) {
          url += `&region=${regionVal}`;
        }

        const data = await fetchAPI(url);

        setRequesters(b => ({ ...b, loading: false, list: data.items }));
      } catch (error) {
        message.error('Failed get buyers');
        setRequesters(b => ({ ...b, loading: false, list: [] }));
      }
    },
    [shouldFilterByRegion]
  );

  const loadEntities = useCallback(
    async (q: string) => {
      setEntities(e => ({ ...e, loading: true }));
      try {
        const data = await fetchAPI('entity/list', {
          method: 'POST',
          payload: { q, page: 1, per_page: 20 },
        });
        setEntities(prev => {
          // keep the option backing the currently selected entity_id so a
          // later fetch never wipes the seeded/selected option
          const merged = [...(data.items || [])];
          const currentId = form.getFieldValue('entity_id');
          const selected = prev.list.find(i => i.id === currentId) || null;
          if (selected && !merged.some(i => i.id === selected.id)) {
            merged.unshift(selected);
          }
          return { list: merged, loading: false };
        });
      } catch (error) {
        message.error('Failed get buyer entities');
        setEntities({ list: [], loading: false });
      }
    },
    [form]
  );

  const loadEntityBuyers = useCallback(async (entityId: number, q = '') => {
    if (!entityId) {
      setBuyers({ list: [], q, loading: false });
      return;
    }
    setBuyers(b => ({ ...b, loading: true }));
    try {
      const data = await fetchAPI(`entity/${entityId}/buyers`, {
        payload: { page: 1, per_page: 50, q },
      });
      setBuyers({ list: data.items || [], q, loading: false });
    } catch (error) {
      message.error('Failed get buyers');
      setBuyers({ list: [], q, loading: false });
    }
  }, []);

  const getProductItems = async payload => {
    return new Promise<any>((resolve, reject) => {
      const {
        custom_products,
        standard_products,
        jd_products,
        amazon_products,
      } = payload;
      const productItems = [
        ...custom_products,
        ...standard_products,
        ...jd_products,
        ...amazon_products,
      ];
      const mergeItems = productItems?.map(product => {
        return {
          id: product?.id,
          name: product?.name,
          quantity: product?.quantity,
          external_url: product?.external_url ?? '',
          external_id: product?.external_id ?? '',
          cost: product?.cost,
          price: product?.price,
          category_item_id: product?.category_item_id,
          tax_rate: Number(product?.tax_rate),
          supplier_id: product?.supplier_id,
          note: product?.note,
        };
      });
      resolve(mergeItems);
    });
  };
  const hasItemChanges = (originalItems, newItems) => {
    const origIds = originalItems.map(i => i.id).filter(Boolean);
    const newIds = newItems.map(i => i.id).filter(Boolean);

    // Add/remove
    if (origIds.length !== newIds.length) return true;
    if (origIds.some(id => !newIds.includes(id))) return true;
    if (newIds.some(id => !origIds.includes(id))) return true;

    // Modifications to existing items
    for (const newItem of newItems) {
      if (!newItem.id) continue;
      const origItem = originalItems.find(o => o.id === newItem.id);
      if (!origItem) continue;
      if (origItem.quantity !== newItem.quantity) return true;
      if (origItem.name !== newItem.name) return true;
      if (origItem.price !== newItem.price) return true;
    }
    return false;
  };

  const onFinish = async payload => {
    if (!payload.buyer_id) {
      message.error('Please select recipient');
      return;
    }

    const onFinishLogic = async () => {
      const hide = message.loading('Saving...', 0);
      try {
        if (!!payload.billing_address_id) {
          payload.address = payload.billing_address_id;
          delete payload.billing_address_id;
        }
        const items = await getProductItems(payload);
        if (items?.length === 0) {
          message.warning('Please select Products');
          hide();
          return;
        }

        const attachments = [
          ...(payload?.attachments_po || []),
          ...(payload?.attachments_do || []),
        ];

        delete payload.attachments_po;
        delete payload.attachments_do;
        delete payload.standard_products;
        delete payload.custom_products;
        delete payload.jd_products;
        delete payload.amazon_products;
        if (!payload.external_po_number) {
          delete payload.external_po_number;
        }
        if (!payload.purchase_request_number) {
          delete payload.purchase_request_number;
        }

        delete payload.entity_id;

        const product = await fetchAPI('order/create_or_edit', {
          payload: {
            attachments,
            id,
            ...payload,
            items,
            currency,
            ...manualRequester,
          },
          method: 'POST',
        });
        form.resetFields();
        props.onOk(product);
        message.success('Save success');
      } catch (e) {
        message.error(`Save failed: ${e}`);
      } finally {
        hide();
      }
    };

    // Reason prompt: only for existing orders (edit mode)
    if (id) {
      const originalOrder = await getOrderDetail(id);
      const originalItems = originalOrder.items;
      const newItems = await getProductItems(payload);

      if (hasItemChanges(originalItems, newItems)) {
        const reason = await new Promise(resolve => {
          changeReasonPromiseRef.current = resolve;
          setSelectedReason('');
          setCustomReason('');
          setChangeReasonModalOpen(true);
        });
        if (!reason) return; // User cancelled
        payload.change_reason = reason;
      }
    }

    if (
      id &&
      (payload.payment_status === OrderPaymentStatus.PAID ||
        payload.payment_status === OrderPaymentStatus.PENDING_PAYMENT)
    ) {
      const originalOrder = await getOrderDetail(id);
      const originalItems = originalOrder.items;
      const newItems = await getProductItems(payload);

      const quantityChanged = newItems.some(newItem => {
        if (!newItem.id) return false;
        const originalItem = originalItems.find(orig => orig.id === newItem.id);
        return originalItem && originalItem.quantity !== newItem.quantity;
      });

      if (quantityChanged) {
        Modal.confirm({
          title: 'Warning',
          content:
            'Quantity cannot be updated once payment status are paid and pending payment',
          okText: 'Change Anyway',
          cancelText: 'Cancel',
          onOk: () => {
            form.setFieldsValue({
              force_change_item: true,
            });
            payload.force_change_item = true;
            onFinishLogic();
          },
          onCancel: () => {},
        });
      } else {
        await onFinishLogic();
      }
    } else {
      await onFinishLogic();
    }
  };

  // Hide DO validation
  // const validatedPoOnOrderCompleteStatus = () => {
  //   const doAttachment = form.getFieldValue('attachments_do');
  //   const hasPO = doAttachment?.length > 0 ? true : false;
  //   if (!hasPO) {
  //     form.setFieldsValue({ operation_status: operationStatus });
  //     Modal.warning({
  //       title: 'Please upload DO',
  //       content: 'To complete the Order, please upload valid DO',
  //       onOk: () => {
  //         form.setFieldsValue({ operation_status: operationStatus });
  //       },
  //     });
  //   }
  //   return hasPO;
  // };

  const updateTotals = () => {
    const customProduct = form.getFieldValue('custom_products') ?? [];
    const standardProduct = form.getFieldValue('standard_products') ?? [];
    const jdProduct = form.getFieldValue('jd_products') ?? [];
    const amazonProduct = form.getFieldValue('amazon_products') ?? [];
    const items = [
      ...customProduct,
      ...standardProduct,
      ...jdProduct,
      ...amazonProduct,
    ];
    const subtotal = sumBy(items, 'total') || 0;
    const tax = form.getFieldValue('tax') ?? 0;
    const handlingFee = form.getFieldValue('handling_fee') ?? 0;
    setSubtotal(subtotal);
    const _total = subtotal + tax + handlingFee;
    const selectedRegion = form.getFieldValue('region');
    setTotal(selectedRegion === 'JP' ? Math.round(_total) : _total);
  };

  useEffect(() => {
    const allAttachments = form.getFieldValue('attachments') || [];
    if (allAttachments?.length > 0) {
      const otherAttachments = [];
      const DoAttachments = [];
      allAttachments.forEach(attachment => {
        const file = attachment?.url?.split('/');
        if (file && file[5] === 'order-do') {
          DoAttachments.push(attachment);
        } else {
          otherAttachments.push(attachment);
        }
      });
      form.setFieldsValue({ attachments_po: [...otherAttachments] });
      form.setFieldsValue({ attachments_do: [...DoAttachments] });
    }

    const productItems = form.getFieldValue('items') ?? [];
    // const parentTaxRate = form.getFieldValue('tax_rate') ?? 0;
    // const parentTaxRate = form.getFieldValue('region_tax_rate') ?? 0;
    const standardProducts = [];
    const customProducts = [];
    const jdProducts = [];
    const amazonProducts = [];

    productItems.forEach(product => {
      if (product?.external_url === 'jd.com') {
        jdProducts.push(product);
      } else if (product?.external_id) {
        standardProducts.push(product);
      } else {
        customProducts.push(product);
      }
    });
    form.setFieldsValue({ standard_products: [...standardProducts] });
    form.setFieldsValue({ custom_products: [...customProducts] });
    form.setFieldsValue({ jd_products: [...jdProducts] });
    form.setFieldsValue({ amazon_products: [...amazonProducts] });
    setIsNcpr(form.getFieldValue('is_ncpr'));

    const addressFromEmailReader = form.getFieldValue(
      'shipping_address_from_er'
    );
    if (!!addressFromEmailReader) {
      setSelectedBuyerAddresses([
        {
          address_name: addressFromEmailReader,
        },
      ]);
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    clearTimeout(buyerTimer.current);
    if (!buyers.q) return; // don't auto-populate on entity change

    buyerTimer.current = setTimeout(() => {
      if (selectedEntity?.id) {
        loadEntityBuyers(selectedEntity.id, buyers.q);
      } else {
        getBuyers(buyers.q, region);
      }
    }, 300);
  }, [buyers.q, selectedEntity, loadEntityBuyers, getBuyers, region]);

  useEffect(() => {
    if (requesterTimer.current) {
      clearTimeout(requesterTimer.current);
    }

    requesterTimer.current = setTimeout(() => {
      getRequesters(requesters.q, region);
    }, 300);
  }, [requesters.q, region, getRequesters]);

  useEffect(() => {
    clearTimeout(entityTimer.current);
    entityTimer.current = setTimeout(() => {
      loadEntities(entityFilters.q);
    }, 300);
  }, [entityFilters, loadEntities]);

  // seed entity/recipient selection when editing an existing order: read the
  // buyer/entity straight from the order detail (entity_buyer + entity)
  // instead of guessing via a separate buyer search request
  useEffect(() => {
    const seedEntityFromOrder = async () => {
      const orderId = form.getFieldValue('id');
      if (!orderId) return;
      try {
        const detail = await getOrderDetail(orderId);
        const entity = detail?.entity;
        const entityBuyer = detail?.entity_buyer;
        if (!entity?.id) return;
        const matched =
          entities.list.find((item: EntityOption) => item.id === entity.id) ||
          null;
        const entityOption =
          matched ||
          ({
            id: entity.id,
            name: entity.name,
            region: entity.region,
            payment_due_in_days: entity.payment_due_in_days,
            billing_address_1: entity.billing_address_1,
            billing_addresses: entity.billing_addresses,
            general_config: entity.general_config,
          } as EntityOption);
        form.setFieldsValue({ entity_id: entity.id });
        setSelectedEntity(entityOption);
        setEntities(e =>
          e.list.some(item => item.id === entityOption.id)
            ? e
            : { ...e, list: [entityOption, ...e.list] }
        );
        // also seed the buyer list so the Recipient Select renders the buyer
        // name even before any search happens
        if (entityBuyer?.id) {
          setBuyers(b => ({
            ...b,
            list: b.list.some(item => item.id === entityBuyer.id)
              ? b.list
              : [
                  {
                    id: entityBuyer.id,
                    name: entityBuyer.name,
                    email: entityBuyer.email,
                    phone_number: entityBuyer.phone_number,
                    shipping_addresses: entityBuyer.shipping_addresses || [],
                    entity: entityOption,
                  },
                  ...b.list,
                ],
          }));
          setShippingOptions(entityBuyer.shipping_addresses || []);
          setRecipientValue(String(entityBuyer.id));
        }
        // prefill addresses + decimal point from the same order-detail
        // payload; no second buyer search request needed
        setSelectedBuyerAddresses(
          [
            { address_name: form.getFieldValue('shipping_address_from_er') },
            ...(entity.billing_addresses || []),
          ].filter(addr => !!addr?.address_name)
        );
        setDecimalPoint(entity?.general_config?.invoice_item_dp || 2);
      } catch (error) {
        // leave entity unset; the user can pick it manually
      }
    };
    seedEntityFromOrder();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getTaxRate = async selectedRegion => {
    let taxRate = 0;
    try {
      const taxResp: {
        id: number;
        region_code: string;
        region: string;
        tax_rate: '8';
        other_rate: '7';
        remarks: string;
      } = await fetchAPI('tax-rates/region/' + selectedRegion, {
        payload: { per_page: 50 },
      });

      taxRate = Number(taxResp?.tax_rate || 0);
    } catch (error) {
      taxRate = 0;
    }

    form.setFieldsValue({ tax_rate: taxRate });
    form.setFieldsValue({ region_tax_rate: taxRate });

    if (useRegionTax) {
      const standardProducts = form.getFieldValue('standard_products') || [];
      const customProducts = form.getFieldValue('custom_products') || [];
      const jdProducts = form.getFieldValue('jd_products') || [];
      const amazonProducts = form.getFieldValue('amazon_products') || [];
      form.setFieldsValue({
        standard_products: standardProducts.map(item => ({
          ...item,
          tax_rate: taxRate,
        })),
      });
      form.setFieldsValue({
        custom_products: customProducts.map(item => ({
          ...item,
          tax_rate: taxRate,
        })),
        jd_products: jdProducts.map(item => ({
          ...item,
          tax_rate: taxRate,
        })),
        amazon_products: amazonProducts.map(item => ({
          ...item,
          tax_rate: taxRate,
        })),
      });
    }
  };

  useEffect(() => {
    if (region) {
      getTaxRate(region);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region]);

  // belt-and-braces: render the selected entity option even if a later
  // entity/list fetch has not re-added it to the list yet
  const entityIdValue = form.getFieldValue('entity_id');
  const displayList =
    entityIdValue &&
    selectedEntity &&
    selectedEntity.id === entityIdValue &&
    !entities.list.some(i => i.id === entityIdValue)
      ? [selectedEntity, ...entities.list]
      : entities.list;

  const handleCreateBuyer = async (payload: NewBuyerPayload) => {
    try {
      const resp = await fetchAPI('entity/buyer/upsert', {
        method: 'POST',
        payload: {
          name: payload.name,
          email: payload.email,
          phone_number: payload.phone_number || undefined,
          shipping_addresses: (payload.shipping_addresses || []).filter(
            (addr: string) => !!addr
          ),
          entity_id: selectedEntity?.id,
        },
      });
      const created = resp?.body;
      setCreateBuyerOpen(false);
      entityBuyerForm.resetFields();
      if (!created) {
        // note: upsert accepted but body missing — refresh via the search
        if (selectedEntity?.id) loadEntityBuyers(selectedEntity.id, buyers.q);
        return;
      }
      setRecipientValue(String(created.id));
      form.setFieldsValue({
        buyer_id: created.id,
        buyer_email: created.email,
        buyer_name: created.name,
        buyer_phone_number: created.phone_number,
        entity_name: selectedEntity?.name,
        address: selectedEntity?.billing_address_1,
        shipping_address: (created.shipping_addresses || [])[0] ?? null,
        payment_due_in_days: selectedEntity?.payment_due_in_days ?? 0,
      });
      setBuyers(b => ({
        ...b,
        q: '',
        loading: false,
        list: [created, ...b.list],
      }));
      setShippingOptions(created.shipping_addresses || []);
      setSelectedBuyerAddresses(
        [
          { address_name: form.getFieldValue('shipping_address_from_er') },
          ...(selectedEntity?.billing_addresses ?? []),
        ].filter(addr => !!addr?.address_name)
      );
      setDecimalPoint(selectedEntity?.general_config?.invoice_item_dp ?? 2);
      message.success('Entity buyer created');
    } catch (error) {
      // keep the modal open so the input can be corrected
      message.error('Failed to create entity buyer');
    }
  };

  return (
    <Form
      {...layout}
      form={form}
      name="basic"
      onFinish={onFinish}
      validateMessages={validateMessages}
    >
      <Form.Item name="region" label="Region" rules={[{ required: true }]}>
        <Select
          placeholder="Please select a region"
          onChange={(val: any) => {
            setRegion(val);
            setCurrency(REGIONS[val]);
          }}
        >
          {(user.regions || []).map(regi => {
            const key = regi.trim();

            return (
              <Select.Option value={key}>
                {`${key} (${REGIONS[key]})`}
              </Select.Option>
            );
          })}
        </Select>
      </Form.Item>
      <Form.Item label=" " colon={false}>
        <Checkbox
          checked={shouldFilterByRegion}
          onChange={e => setShouldFilterByRegion(e.target.checked)}
        >
          Filter Buyers/Requesters by Selected Region
        </Checkbox>
      </Form.Item>
      <Form.Item label="Override Currency" rules={[{ required: true }]}>
        <Select
          placeholder="Please select currency"
          disabled={!form.getFieldValue('region')}
          onChange={(val: any) => {
            setCurrency(val);
          }}
          value={currency}
          showSearch
        >
          {Object.values(REGIONS).map(currCurrency => {
            return (
              <Select.Option value={currCurrency}>{currCurrency}</Select.Option>
            );
          })}
        </Select>
      </Form.Item>

      <Form.Item
        label="PO Issue Date"
        name="issue_date"
        rules={[{ type: 'object', required: true }]}
      >
        <DatePicker />
      </Form.Item>

      {/* HIDDEN BECAUSE INVOICE DATE WILL BE GENERATED BY SYSTEM */}
      {/*
        UPDATE 19 Dec 2023
        Show again, because management (Michelle) need to be able
        manually update invoice date before sync to Xero
      */}
      <Form.Item
        // hidden
        label="Invoice Date"
        name="invoice_date"
        // rules={[{ type: 'object', required: true }]}
      >
        {/* <DatePicker disabled={!isFinance} /> */}
        <DatePicker />
      </Form.Item>
      {/* HIDDEN BECAUSE PAYMENT TERMS WILL USE BUYER'S ENTITY PAYMENT TERMS */}
      {/* <Form.Item
        name="payment_due_in_days"
        label="Payment Terms"
        rules={[{ required: true }]}
      >
        <Select placeholder="Please select a payment terms">
          {[0, 7, 14, 15, 30, 45, 60].map(key => (
            <Select.Option value={key}>{`${key} Days`}</Select.Option>
          ))}
        </Select>
      </Form.Item> */}
      <Form.Item
        label="RIC Assignee"
        name="assignee_id"
        rules={[{ required: true }]}
      >
        <Select
          showSearch
          optionFilterProp="children"
          placeholder="Select an RIC user"
        >
          {(users || []).map(user => (
            <Select.Option value={user.id}>
              {user.full_name} - {user.username}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      <div onDoubleClick={() => setShowEntityName(v => !v)}>
        <Form.Item label="Entity" name="entity_id">
          <Select
            showSearch
            filterOption={false}
            placeholder="Select an entity first"
            loading={entities.loading}
            notFoundContent={
              entities.loading ? 'Loading...' : 'No entity found'
            }
            onSearch={val => {
              debounceEntityQ.cancel();
              debounceEntityQ(val);
            }}
            allowClear
            onChange={(val: number) => {
              form.setFieldsValue({
                buyer_id: undefined,
                buyer_email: undefined,
                buyer_name: undefined,
                buyer_phone_number: undefined,
                entity_name: undefined,
                address: undefined,
                shipping_address: undefined,
                billing_address_id: undefined,
              });
              setBuyers({ list: [], q: '', loading: false });
              setRecipientValue(undefined);
              const matched =
                entities.list.find(item => item.id === val) || null;
              setSelectedEntity(matched);
            }}
          >
            {displayList.map(item => (
              <Select.Option value={item.id} key={item.id}>
                [{item.id}][{item.name}][{item.region}]
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </div>

      <Form.Item label="Recipient" required>
        <Select
          showSearch
          searchValue={buyers.q}
          onSearch={val => setBuyers(b => ({ ...b, q: val }))}
          value={recipientValue}
          filterOption={false}
          onChange={value => {
            // @ts-ignore
            const buyer = find(buyers.list, ['id', Number(value)]);
            const address = buyer.shipping_addresses || [];
            // entity/<id>/buyers rows carry a flat entity_id; buyer search rows do not
            const buyerRow = buyer as typeof buyer & { entity_id?: number };
            const buyerEntityId = buyerRow.entity_id ?? buyer?.entity?.id;
            form.setFieldsValue({
              buyer_id: buyer.id,
              buyer_email: buyer.email,
              buyer_name: buyer.name,
              buyer_phone_number: buyer.phone_number,
              entity_name: selectedEntity?.name ?? buyer?.entity?.name,
              address:
                selectedEntity?.billing_address_1 ??
                buyer?.entity?.billing_address_1,
              shipping_address: isEmpty(address) ? null : address[0],
              payment_due_in_days:
                selectedEntity?.payment_due_in_days ??
                buyer?.entity?.payment_due_in_days ??
                0,
              ...(!selectedEntity && buyerEntityId
                ? { entity_id: buyerEntityId }
                : {}),
            });
            if (!selectedEntity && buyerEntityId) {
              const buyerEntity =
                buyer?.entity ??
                ({
                  id: buyerEntityId,
                  name: buyer?.entity?.name,
                } as EntityOption);
              setSelectedEntity(buyerEntity);
              setEntities(e =>
                e.list.some(item => item.id === buyerEntityId)
                  ? e
                  : { ...e, list: [buyerEntity, ...e.list] }
              );
            }
            setShippingOptions(address);
            setSelectedBuyerAddresses(
              [
                {
                  address_name: form.getFieldValue('shipping_address_from_er'),
                },
                ...(selectedEntity?.billing_addresses ??
                  buyer?.entity?.billing_addresses ??
                  []),
              ].filter(obj => !!obj?.address_name)
            );
            setDecimalPoint(
              selectedEntity?.general_config?.invoice_item_dp ??
                buyer?.entity?.general_config?.invoice_item_dp ??
                2
            );
            setRecipientValue(String(value));
          }}
          placeholder="Select a recipient"
        >
          {(buyers.loading ? [] : buyers.list).map(buyer => (
            <Select.Option value={String(buyer.id)}>
              <Tooltip
                title={`Entity: ${selectedEntity?.name} [${selectedEntity?.id}]`}
              >
                <span>
                  [{buyer.id}] {buyer.name} | {buyer.email} | [
                  {selectedEntity?.id}] {selectedEntity?.name}
                </span>
              </Tooltip>
            </Select.Option>
          ))}
        </Select>
        {buyers.q &&
          !buyers.loading &&
          !buyers.list.length &&
          !!selectedEntity?.id && (
            <Button
              type="link"
              size="small"
              style={{ paddingLeft: 0 }}
              onClick={() => setCreateBuyerOpen(true)}
            >
              Create entity buyer
            </Button>
          )}
      </Form.Item>
      <Form.Item label="Requester">
        <Select
          showSearch
          searchValue={requesters.q}
          onSearch={val => setRequesters(b => ({ ...b, q: val }))}
          defaultValue={requesterId || undefined}
          filterOption={false}
          placeholder="Select a requester"
          onSelect={value => {
            const requester = find(requesters.list, ['id', Number(value)]);
            setManualRequester({
              requester_id: requester.id,
              requester_email: requester.email,
              requester_name: requester.name,
              requester_phone_number: requester.phone_number,
            });
          }}
        >
          {(requesters.loading ? [] : requesters.list).map(requester => (
            <Select.Option value={requester.id}>
              <Tooltip
                title={`Entity: ${requester.entity?.name} [${requester.entity?.id}]`}
              >
                <span>
                  [{requester.id}] {requester.name} | {requester.email} | [
                  {requester.entity?.id}] {requester.entity?.name}
                </span>
              </Tooltip>
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item label="Use Default Billing Address ">
        <Switch
          checked={isUseDefaultBillingAddress}
          onChange={value => setIsUseDefaultBillingAddress(value)}
          checkedChildren="Yes"
          unCheckedChildren="No"
        />
      </Form.Item>
      {isUseDefaultBillingAddress ? (
        <Form.Item
          label="Billing Address"
          name="address"
          rules={[{ type: 'string' }]}
        >
          <Input disabled={true} />
        </Form.Item>
      ) : (
        <Form.Item label="Billing Address" name="billing_address_id">
          <Select
            showSearch
            optionFilterProp="children"
            placeholder="Select a billing address"
          >
            {selectedBuyerAddresses.map(addr => (
              <Select.Option value={addr.address_name}>
                {addr.address_name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      )}
      <Form.Item
        label="FIC Assignee"
        name="finance_assignee_id"
        rules={[{ required: true }]}
      >
        <Select
          showSearch
          optionFilterProp="children"
          placeholder="Select a FIC user"
        >
          {(users || []).map(user => (
            <Select.Option value={user.id}>
              {user.full_name} - {user.username}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      {/* Hidden Field */}
      <Form.Item
        style={{ display: 'none' }}
        name="buyer_id"
        rules={[{ required: true }]}
      >
        <Input disabled={true} />
      </Form.Item>
      {/* END Hidden Field */}
      <Form.Item
        label="Recipient Email "
        name="buyer_email"
        rules={[{ type: 'email' }]}
      >
        <Input disabled={true} />
      </Form.Item>
      <Form.Item
        label="Recipient Name"
        name="buyer_name"
        rules={[{ type: 'string' }]}
      >
        <Input disabled={true} />
      </Form.Item>
      <Form.Item
        label="Recipient Phone Number"
        name="buyer_phone_number"
        rules={[{ type: 'string', required: false }]}
      >
        <Input disabled={true} />
      </Form.Item>
      <Form.Item
        label="Entity Name"
        name="entity_name"
        style={{ display: showEntityName ? undefined : 'none' }}
        rules={[{ type: 'string', required: false }]}
      >
        <Input disabled={!showEntityName} />
      </Form.Item>
      <Form.Item
        label="Payment Terms"
        name="payment_due_in_days"
        rules={[{ type: 'number' }]}
      >
        <Input disabled={true} />
      </Form.Item>
      <Form.Item
        label="Shipping Address"
        name="shipping_address"
        rules={[{ type: 'string', required: true }]}
      >
        <Select
          onChange={value => {
            form.setFieldsValue({
              shipping_address: value,
            });
          }}
          placeholder="Select a shipping address"
        >
          {shippingOptions.map(value => (
            <Select.Option value={value}>{value}</Select.Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item
        label="Use Region Tax"
        name="is_region_tax"
        rules={[{ type: 'boolean' }]}
      >
        <Switch
          checked={useRegionTax}
          onChange={value => setUseRegionTax(value)}
          checkedChildren="Yes"
          unCheckedChildren="No"
        />
      </Form.Item>
      <Form.Item
        label="Invoice Reference Number"
        name="invoice_reference"
        rules={[{ type: 'string' }]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label="Exclude from DHL Report"
        name="is_excluded_from_reports"
      >
        <Switch
          defaultChecked={form.getFieldValue('is_excluded_from_reports')}
          checkedChildren="Yes"
          unCheckedChildren="No"
        />
      </Form.Item>
      <Row style={{ marginTop: -24, marginBottom: 24 }}>
        <Col span={8} />
        <Col span={16}>
          <ExcludedReportInfo>
            <p>
              <InfoCircleOutlined /> Please put yes if the order value is more
              than:
            </p>
            <ul>
              <li>SG: 5000 EURO</li>
              <li>CN: 2000 RMB</li>
              <li>All Other Regions: 1500 EURO</li>
            </ul>
          </ExcludedReportInfo>
        </Col>
      </Row>
      <Form.Item
        label="Exclude from Xero Sync"
        name="is_excluded_from_xero_sync"
      >
        <Switch
          defaultChecked={form.getFieldValue('is_excluded_from_xero_sync')}
          checkedChildren="Yes"
          unCheckedChildren="No"
        />
      </Form.Item>
      <Row style={{ marginTop: -24, marginBottom: 24 }}>
        <Col span={8} />
        <Col span={16}>
          <ExcludedReportInfo>
            <p>
              <InfoCircleOutlined />
            </p>
            <ul>
              <li>SG, MY, AU, ID, TH: No</li>
              <li>Other regions: Yes</li>
            </ul>
          </ExcludedReportInfo>
        </Col>
      </Row>
      <Divider orientation="left" plain>
        Payment
      </Divider>
      {/* <Form.Item
        name="operation_status"
        label="Operation Status"
        rules={[{ required: true }]}
      >
        <Select
          placeholder="Please select status"
          onChange={status => {
            // if (status === OrderOperationStatus?.COMPLETED) {
            //   validatedPoOnOrderCompleteStatus();
            // }
            form.setFieldsValue({ operation_status: status });
          }}
        >
          {Object.keys(OrderOperationStatus).map(key => (
            <Select.Option value={OrderOperationStatus[key]}>
              <Tag color={ColorMap[OrderOperationStatus[key]]}>
                {OrderOperationStatus[key]}
              </Tag>
            </Select.Option>
          ))}
        </Select>
      </Form.Item> */}
      <Form.Item
        name="payment_status"
        label="Payment Status"
        rules={[{ required: true }]}
      >
        <Select placeholder="Please select a country">
          {Object.keys(OrderPaymentStatus).map(key => (
            <Select.Option value={OrderPaymentStatus[key]}>
              <Tag color={ColorMap[OrderPaymentStatus[key]]}>
                {OrderPaymentStatus[key]}
              </Tag>
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item
        label="Remarks"
        name="remarks"
        rules={[{ type: 'string', required: false }]}
      >
        <Input.TextArea />
      </Form.Item>
      <Form.Item label={`Invoice Subtotal Amount (${currency})`}>
        <Input disabled={true} value={subtotal} />
      </Form.Item>
      <Form.Item
        label={`Invoice Tax Amount (${currency})`}
        name="tax"
        rules={[{ type: 'number', required: true }]}
      >
        <Tooltip
          trigger={['focus']}
          title={'Please make sure it is not double input with item tax'}
          placement="topLeft"
          overlayClassName="numeric-input"
        >
          <InputNumber onChange={() => updateTotals()} />
        </Tooltip>
      </Form.Item>
      {/* HIDDEN, BCAUSE FOR NOW WE GET TAX FROM REGIONAL TAX */}
      {/* <Form.Item
        label={`Tax Rate`}
        name="tax_rate"
        rules={[{ type: 'number' }]}
      >
        <InputNumber onChange={() => updateTotals()} />
      </Form.Item> */}
      <Form.Item
        label={`Handling Fee (${currency})`}
        name="handling_fee"
        rules={[{ type: 'number', required: true }]}
      >
        <InputNumber onChange={() => updateTotals()} />
      </Form.Item>
      <Form.Item label={`Invoice Total Amount (${currency})`}>
        <Input disabled={true} value={total} />
      </Form.Item>
      <Divider orientation="left" plain>
        PO Info
      </Divider>

      <Form.Item
        label="Is Without PO"
        name="is_without_po"
        valuePropName="checked"
      >
        <Switch
          // defaultChecked={form.getFieldValue('is_without_po')}
          checkedChildren="Yes"
          unCheckedChildren="No"
        />
      </Form.Item>
      <Form.Item
        label="Is Consolidated Line items"
        name="is_consolidated_line_items"
        valuePropName="checked"
      >
        <Switch checkedChildren="Yes" unCheckedChildren="No" />
      </Form.Item>
      <Form.Item label="Upload files">
        <Form.Item
          name="attachments_po"
          valuePropName="fileList"
          getValueFromEvent={normFile}
          noStyle
        >
          <Upload
            showUploadList={{
              showDownloadIcon: true,
              showRemoveIcon: true,
            }}
            withCredentials
            multiple
            listType="picture"
            data={{ type: 'order' }}
            action={getFullAPIURLWithEndpoint('upload', 'v1.0')}
            headers={{ 'X-CSRF-TOKEN': Cookies.get('csrf_access_token') || '' }}
            defaultFileList={defaultFileList}
          >
            <Button>
              <UploadOutlined /> Upload
            </Button>
          </Upload>
        </Form.Item>
        <div style={{ marginTop: '10px' }}>
          <Alert
            message={
              <span style={{ fontSize: '12px' }}>
                Please add Purchase Order document here. EX filename: SAID1111
                PO3348952
              </span>
            }
            type="warning"
            showIcon
          />
        </div>
        <OrderSyncSMEPODocuments
          orderId={id}
          onSuccess={async () => {
            if (props.refreshDetailData) {
              await props.refreshDetailData();
            }
          }}
          hasAttachmentPO={hasAttachmentPO}
        />
      </Form.Item>

      {/* <Form.Item label="Upload DO">
        <Form.Item
          name="attachments_do"
          valuePropName="fileList"
          getValueFromEvent={normFile}
          noStyle
        >
          <Upload
            showUploadList={{
              showDownloadIcon: true,
              showRemoveIcon: true,
            }}
            withCredentials
            multiple
            listType="picture"
            data={{ type: 'order-do' }}
            action={getFullAPIURLWithEndpoint('upload', 'v1.0')}
            headers={{ 'X-CSRF-TOKEN': Cookies.get('csrf_access_token') || '' }}
          >
            <Button>
              <UploadOutlined /> Upload DO
            </Button>
          </Upload>
        </Form.Item>
      </Form.Item> */}

      {/* <Form.Item
        label="Category"
        name="category"
        rules={[{ type: 'string', required: true }]}
      >
        <TreeSelect
          loading={categories.loading}
          showSearch
          allowClear
          style={{ width: '100%' }}
          placeholder="Select 99sme Category"
          dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
          treeData={treeDataForCategories}
          treeDefaultExpandAll
        />
      </Form.Item> */}
      <Form.Item
        label={
          <span>
            PO Reference Number{' '}
            <Tooltip title="PO Reference Number from SME Marketplace">
              <InfoCircleOutlined />
            </Tooltip>
          </span>
        }
        name="po_reference_number"
        rules={[{ type: 'string', required: true }]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label={
          <span>
            External PO Number{' '}
            <Tooltip title="External PO Number. eg: PO Number from TikTok">
              <InfoCircleOutlined />
            </Tooltip>
          </span>
        }
        name="external_po_number"
      >
        <Input />
      </Form.Item>
      <Form.Item
        label={
          <span>
            Purchase Request Number
            <Tooltip title="Purchase Request Number">
              <InfoCircleOutlined />
            </Tooltip>
          </span>
        }
        name="purchase_request_number"
      >
        <Input />
      </Form.Item>
      {/* <Form.List name="numbers">
        {(fields, { add, remove }) => {
          return (
            <div>
              {fields.map((field, index) => (
                <Form.Item
                  {...(index === 0 ? layout : formItemLayoutWithOutLabel)}
                  label={index === 0 ? 'PO Number' : ''}
                  required
                  key={field.key}
                >
                  <Form.Item
                    {...field}
                    validateTrigger={['onChange', 'onBlur']}
                    rules={[
                      {
                        required: true,
                        whitespace: true,
                        message: 'Please input po number or delete this field.',
                      },
                    ]}
                    noStyle
                  >
                    <Input style={{ width: '60%', marginRight: 8 }} />
                  </Form.Item>
                  {fields.length > 1 ? (
                    <MinusCircleOutlined
                      className="dynamic-delete-button"
                      onClick={() => {
                        remove(field.name);
                      }}
                    />
                  ) : null}
                </Form.Item>
              ))}
              <Form.Item {...formItemLayoutWithOutLabel}>
                <Button
                  type="dashed"
                  onClick={() => {
                    add();
                  }}
                  style={{ width: '60%' }}
                >
                  <PlusOutlined /> Add PO Number
                </Button>
              </Form.Item>
            </div>
          );
        }}
      </Form.List> */}
      {/* <Form.Item {...formItemLayoutWithOutLabel} label="Product" required>
        <div />
      </Form.Item> */}
      <Form.Item
        label="Is this an NCPR order?"
        name="is_ncpr"
        rules={[{ type: 'boolean' }]}
      >
        <Switch
          checked={isNcpr}
          onChange={value => setIsNcpr(value)}
          checkedChildren="Yes"
          unCheckedChildren="No"
        />
      </Form.Item>
      <Form.Item name="force_change_item" hidden initialValue={false}>
        <Input />
      </Form.Item>
      <Divider>Products</Divider>
      <div style={{ marginTop: '20px', gap: '15px', display: 'flex' }}>
        <Button
          onClick={() => {
            customProductCreateBtn?.current.click();
          }}
        >
          Add Custom Product
        </Button>
        <Button
          onClick={() => {
            standardProductCreateBtn?.current.click();
          }}
        >
          Add Product
        </Button>
        {region === 'CN' && (
          <Button
            onClick={() => {
              jdProductCreateBtn?.current.click();
            }}
          >
            Add JD Product
          </Button>
        )}
        {region === 'JP' && (
          <Button
            onClick={() => {
              amazonProductCreateBtn?.current.click();
            }}
          >
            Add Amazon JP Product
          </Button>
        )}
      </div>

      {/* Custom Products */}
      <OrderProductList
        form={form}
        updateTotals={updateTotals}
        region={region}
        fieldName="custom_products"
        categories={categories}
        useRegionTax={useRegionTax}
        formItemLayoutWithOutLabel={formItemLayoutWithOutLabel}
        buttonRef={customProductCreateBtn}
        decimalPoint={decimalPoint}
        subtotalPriceAll={form.getFieldValue('subtotal_price_all')}
      />

      {/* Standard Products */}
      <OrderProductList
        form={form}
        updateTotals={updateTotals}
        region={region}
        fieldName="standard_products"
        categories={categories}
        useRegionTax={useRegionTax}
        formItemLayoutWithOutLabel={formItemLayoutWithOutLabel}
        buttonRef={standardProductCreateBtn}
        decimalPoint={decimalPoint}
        subtotalPriceAll={form.getFieldValue('subtotal_price_all')}
      />

      {/* JD Products */}
      <OrderProductList
        form={form}
        updateTotals={updateTotals}
        region={region}
        fieldName="jd_products"
        categories={categories}
        useRegionTax={useRegionTax}
        formItemLayoutWithOutLabel={formItemLayoutWithOutLabel}
        buttonRef={jdProductCreateBtn}
        decimalPoint={decimalPoint}
        subtotalPriceAll={form.getFieldValue('subtotal_price_all')}
      />

      {/* Amazon Products */}
      <OrderProductList
        form={form}
        updateTotals={updateTotals}
        region={region}
        fieldName="amazon_products"
        categories={categories}
        useRegionTax={useRegionTax}
        formItemLayoutWithOutLabel={formItemLayoutWithOutLabel}
        buttonRef={amazonProductCreateBtn}
        decimalPoint={decimalPoint}
        subtotalPriceAll={form.getFieldValue('subtotal_price_all')}
      />
      {subtotal > 0 && (
        <div style={{ width: '100%', marginTop: 10, textAlign: 'center' }}>
          <b>Total Amount (Incl. Tax):</b>{' '}
          {parseFloat(String(total)).toFixed(2)} {currency}
        </div>
      )}

      <div style={{ padding: '100px' }} />
      <Modal
        title="Item Change Reason"
        visible={changeReasonModalOpen}
        onOk={() => {
          const finalReason =
            selectedReason === '__other__' ? customReason : selectedReason;
          if (!finalReason) {
            message.error('Please select or enter a reason');
            return;
          }
          changeReasonPromiseRef.current(finalReason);
          setChangeReasonModalOpen(false);
        }}
        onCancel={() => {
          changeReasonPromiseRef.current(null);
          setChangeReasonModalOpen(false);
        }}
      >
        <p>Please provide a reason for the changes you made:</p>
        <Select
          style={{ width: '100%' }}
          placeholder="Select a reason"
          onChange={(value: any) => setSelectedReason(value as string)}
        >
          {CHANGE_REASON_OPTIONS.map(opt => (
            <Select.Option key={opt.value} value={opt.value}>
              {opt.label}
            </Select.Option>
          ))}
        </Select>
        {selectedReason === '__other__' && (
          <Input.TextArea
            style={{ marginTop: 8, width: '100%' }}
            rows={3}
            placeholder="Please specify the reason..."
            onChange={e => setCustomReason(e.target.value)}
          />
        )}
      </Modal>

      <Modal
        title="Create entity buyer"
        visible={createBuyerOpen}
        okText="Create"
        destroyOnClose
        onCancel={() => {
          setCreateBuyerOpen(false);
          entityBuyerForm.resetFields();
        }}
        onOk={() => entityBuyerForm.submit()}
      >
        <Form
          form={entityBuyerForm}
          name="createEntityBuyer"
          layout="vertical"
          onFinish={handleCreateBuyer}
        >
          <Form.Item
            label="Name"
            name="name"
            rules={[{ type: 'string', required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Email"
            name="email"
            rules={[{ type: 'email', required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Phone Number"
            name="phone_number"
            rules={[{ type: 'string', required: false }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Shipping Addresses"
            name="shipping_addresses"
            rules={[{ type: 'array', required: false }]}
          >
            <Select
              mode="tags"
              dropdownStyle={{ display: 'none' }}
              placeholder="Type an address, press Enter"
            />
          </Form.Item>
        </Form>
      </Modal>
    </Form>
  );
};

const ExcludedReportInfo = styled.div`
  margin-top: 10px;

  > * {
    opacity: 0.7;
  }

  p {
    margin: 0;
  }

  ul {
    margin: 0;
    padding: 0;
    list-style: none;
  }
`;
